import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Inbox,
  Users,
  Mail,
  Phone,
  Calendar,
  FileText,
  Filter,
  Search,
  CheckCircle2,
  Clock,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../components/ui/dialog";
import {
  adminInquiries,
  adminUpdateStatus,
  adminStats,
  adminMe,
  clearToken,
  getToken
} from "../lib/api";
import Logo from "../components/Logo";

const statusColors = {
  new: "bg-amber-100 text-amber-900 border-amber-300",
  contacted: "bg-blue-50 text-blue-900 border-blue-200",
  scheduled: "bg-emerald-50 text-emerald-900 border-emerald-200",
  closed: "bg-stone-100 text-stone-700 border-stone-300",
  lead: "bg-purple-50 text-purple-900 border-purple-200"
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const hasToken = !!getToken();

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, invRes, statsRes] = await Promise.all([
        adminMe(),
        adminInquiries(),
        adminStats()
      ]);
      setAdminEmail(meRes.data?.email || "");
      setInquiries(invRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasToken) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = () => {
    clearToken();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const handleStatusChange = async (id, status) => {
    try {
      await adminUpdateStatus(id, status);
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Marked as ${status}.`);
      // Refresh stats
      adminStats().then(({ data }) => setStats(data)).catch(() => {});
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const filtered = inquiries.filter((i) => {
    const matchStatus = filterStatus === "all" || i.status === filterStatus;
    const matchSource = filterSource === "all" || i.source === filterSource;
    const matchQuery =
      !query ||
      i.name.toLowerCase().includes(query.toLowerCase()) ||
      i.email.toLowerCase().includes(query.toLowerCase()) ||
      (i.phone && i.phone.includes(query));
    return matchStatus && matchSource && matchQuery;
  });

  const statCards = [
    { label: "Total", value: stats?.total ?? 0, Icon: Inbox, color: "text-slate-900" },
    { label: "New", value: stats?.new ?? 0, Icon: Clock, color: "text-amber-700" },
    { label: "Contact form", value: stats?.contact_form ?? 0, Icon: Users, color: "text-slate-900" },
    { label: "Checklist leads", value: stats?.checklist_leads ?? 0, Icon: FileText, color: "text-amber-700" }
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
          <Logo size="small" />
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Mail className="w-4 h-4 text-amber-700" />
              </div>
              <span className="text-xs">{adminEmail}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              data-testid="admin-refresh"
              className="rounded-full border-stone-300"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="rounded-full border-stone-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <p className="text-amber-700 text-xs uppercase tracking-[0.3em] font-semibold mb-3">
            Dashboard
          </p>
          <h1 className="font-serif text-4xl text-slate-900 tracking-tight">
            Family inquiries
          </h1>
          <p className="text-slate-600 mt-2">
            Every inquiry is a family reaching out for help. Respond with care, within 24 hours.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((s, i) => (
            <div
              key={s.label}
              data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white rounded-2xl p-6 border border-stone-200"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold">
                  {s.label}
                </p>
                <s.Icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className={`font-serif text-4xl ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-5 border border-stone-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5 relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                data-testid="admin-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="pl-11 py-5 rounded-xl border-stone-200"
              />
            </div>
            <div className="md:col-span-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="admin-filter-status" className="rounded-xl py-5 border-stone-200">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4">
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger data-testid="admin-filter-source" className="rounded-xl py-5 border-stone-200">
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="contact-form">Contact form</SelectItem>
                  <SelectItem value="checklist-download">Checklist download</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Inquiries table */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-slate-500">Loading inquiries...</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="font-serif text-2xl text-slate-900 mb-2">No inquiries yet</p>
              <p className="text-slate-500">When families reach out, they'll appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left text-xs uppercase tracking-wider font-semibold text-slate-600 px-6 py-4">Name</th>
                    <th className="text-left text-xs uppercase tracking-wider font-semibold text-slate-600 px-6 py-4 hidden md:table-cell">Contact</th>
                    <th className="text-left text-xs uppercase tracking-wider font-semibold text-slate-600 px-6 py-4 hidden lg:table-cell">Source</th>
                    <th className="text-left text-xs uppercase tracking-wider font-semibold text-slate-600 px-6 py-4">Status</th>
                    <th className="text-left text-xs uppercase tracking-wider font-semibold text-slate-600 px-6 py-4 hidden lg:table-cell">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filtered.map((inq) => (
                    <tr
                      key={inq.id}
                      data-testid={`admin-inquiry-row-${inq.id}`}
                      onClick={() => setSelected(inq)}
                      className="hover:bg-amber-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{inq.name}</p>
                        <p className="text-xs text-slate-500 md:hidden">{inq.email}</p>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <p className="text-sm text-slate-700">{inq.email}</p>
                        {inq.phone && <p className="text-xs text-slate-500">{inq.phone}</p>}
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <span className="text-xs text-slate-600">
                          {inq.source === "contact-form" ? "Contact form" : "Checklist"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${statusColors[inq.status] || statusColors.new} border rounded-full text-xs px-3 py-1 capitalize font-medium`}>
                          {inq.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <p className="text-xs text-slate-500">
                          {new Date(inq.created_at).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-stone-50" data-testid="inquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogDescription className="text-amber-700 text-xs uppercase tracking-[0.3em] font-semibold">
                  Inquiry details
                </DialogDescription>
                <DialogTitle className="font-serif text-3xl text-slate-900">
                  {selected.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* Contact */}
                <div className="bg-white rounded-xl p-5 border border-stone-200 space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-amber-700" />
                    <a href={`mailto:${selected.email}`} className="text-slate-900 hover:text-amber-700">
                      {selected.email}
                    </a>
                  </div>
                  {selected.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-amber-700" />
                      <a href={`tel:${selected.phone}`} className="text-slate-900 hover:text-amber-700">
                        {selected.phone}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-amber-700" />
                    <span className="text-slate-700 text-sm">
                      {new Date(selected.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Details */}
                {(selected.recipient_relation || selected.service || selected.hours_needed || selected.timeframe) && (
                  <div className="bg-white rounded-xl p-5 border border-stone-200 space-y-2 text-sm">
                    {selected.recipient_relation && (
                      <div className="flex justify-between py-1">
                        <span className="text-slate-500">Who needs care</span>
                        <span className="text-slate-900 font-medium">{selected.recipient_relation}</span>
                      </div>
                    )}
                    {selected.service && (
                      <div className="flex justify-between py-1">
                        <span className="text-slate-500">Service</span>
                        <span className="text-slate-900 font-medium">{selected.service}</span>
                      </div>
                    )}
                    {selected.hours_needed && (
                      <div className="flex justify-between py-1">
                        <span className="text-slate-500">Hours per week</span>
                        <span className="text-slate-900 font-medium">{selected.hours_needed}</span>
                      </div>
                    )}
                    {selected.timeframe && (
                      <div className="flex justify-between py-1">
                        <span className="text-slate-500">Timeframe</span>
                        <span className="text-slate-900 font-medium">{selected.timeframe}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Message */}
                <div className="bg-amber-50/60 rounded-xl p-5 border border-amber-200/60">
                  <p className="text-xs text-amber-800 uppercase tracking-widest font-semibold mb-2">Message</p>
                  <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                </div>

                {/* Email Status */}
                <div className="flex gap-3 text-xs">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${selected.email_sent_customer ? "bg-emerald-50 text-emerald-900 border-emerald-200" : "bg-stone-100 text-stone-600 border-stone-200"}`}>
                    {selected.email_sent_customer ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    Customer email {selected.email_sent_customer ? "sent" : "not sent"}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${selected.email_sent_admin ? "bg-emerald-50 text-emerald-900 border-emerald-200" : "bg-stone-100 text-stone-600 border-stone-200"}`}>
                    {selected.email_sent_admin ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    Admin email {selected.email_sent_admin ? "sent" : "not sent"}
                  </span>
                </div>
              </div>

              <DialogFooter className="mt-6 gap-2 sm:gap-3 flex-col sm:flex-row">
                <Select
                  value={selected.status}
                  onValueChange={(v) => handleStatusChange(selected.id, v)}
                >
                  <SelectTrigger data-testid="inquiry-status-select" className="rounded-full border-stone-300 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                  </SelectContent>
                </Select>
                <a href={`mailto:${selected.email}`}>
                  <Button className="bg-amber-700 hover:bg-amber-800 text-white rounded-full w-full">
                    <Mail className="w-4 h-4 mr-2" />
                    Reply via email
                  </Button>
                </a>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
